import { db } from './firebase';
import { collection, getDocs, getDoc, doc, query, where, limit } from 'firebase/firestore';

export const hibritService = {
  // Check if hibrit collections exist and have data
  async checkHibritCollections() {
    console.log('🔍 Checking hibrit collections...');

    const collections = ['business_accounts', 'business_info', 'business_tables', 'business_advanced_settings'];
    const results = {};

    for (const collectionName of collections) {
      try {
        const snapshot = await getDocs(query(collection(db, collectionName), limit(5)));
        results[collectionName] = {
          count: snapshot.size,
          documents: snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
        };
        console.log(`📂 Collection ${collectionName}: ${snapshot.size} documents`);
      } catch (error) {
        console.error(`❌ Error checking ${collectionName}:`, error);
        results[collectionName] = { error: error.message };
      }
    }

    return results;
  },

  // Get businesses from hibrit structure for current user - NEW IMPLEMENTATION
  // Get businesses from hibrit structure for current user - OPTIMIZED
  async getBusinessesForUser(userId) {
    try {
      if (!db || !userId) return [];

      console.log('🚀 HibritService - Optimized Fetch for:', userId);

      // SaaS Unified Identity (memberships) support
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const saasMemberships = user.memberships || user.businesses || [];
      
      if (saasMemberships.length === 0) {
        console.log('ℹ️ HibritService - No memberships/businesses found for user');
        return [];
      }

      console.log(`🏗️ HibritService - Loading ${saasMemberships.length} businesses for user...`);
      
      // Use Promise.all to fetch latest business info in parallel
      const businesses = await Promise.all(saasMemberships.map(async (m) => {
        try {
          const rawSector = m.sector || 'food_beverage';
          const sector = rawSector.replace(/-/g, '_');
          const businessId = m.business_id;
          
          if (!businessId) return null;

          // 🎯 FETCH FRESH INFO: Check approved enterprise paths (Optimized: Max 2 reads instead of 14)
          let businessInfo = null;
          
          // 1. Primary Approved Enterprise Path: settings/business_info
          const primaryPath = `businesses/${sector}/businesses/${businessId}/settings/business_info`;
          const primarySnap = await getDoc(doc(db, primaryPath));
          if (primarySnap.exists()) {
            businessInfo = primarySnap.data();
          }

          // 2. Secondary Fallback: direct business document if not found in sub-collection
          if (!businessInfo || (!businessInfo.name && !businessInfo.businessName && !businessInfo.restaurantName && !businessInfo.business_name && !businessInfo.brandName)) {
            const fallbackPath = `businesses/${sector}/businesses/${businessId}`;
            const fallbackSnap = await getDoc(doc(db, fallbackPath));
            if (fallbackSnap.exists()) {
              businessInfo = { ...fallbackSnap.data(), ...businessInfo };
            }
          }
          
          // 3. Legacy Fallback (only if completely empty): root restaurants/businessId
          if (!businessInfo || (!businessInfo.name && !businessInfo.businessName && !businessInfo.restaurantName && !businessInfo.business_name && !businessInfo.brandName)) {
            const legacySnap = await getDoc(doc(db, `restaurants/${businessId}`));
            if (legacySnap.exists()) {
              businessInfo = { ...legacySnap.data(), ...businessInfo };
            }
          }
          
          if (!businessInfo) businessInfo = {};
          
          return {
            id: businessId,
            _id: businessId,
            name: businessInfo.name || businessInfo.businessName || businessInfo.restaurantName || businessInfo.business_name || businessInfo.brandName || m.business_name || 'İşletme',
            logo: businessInfo.logo || businessInfo.business_logo || businessInfo.logo_url || m.business_logo || m.logo || null,
            sector: sector,
            business_type: businessInfo.business_type || m.sector || 'restaurant',
            status: 'active',
            isHibritStructure: true,
            hibritPath: `businesses/${sector}/businesses/${businessId}`,
            firebase_path: `businesses/${sector}/businesses/${businessId}`,
            role: m.role,
            tenant_id: m.tenant_id,
            category_name: businessInfo.category_name || businessInfo.faaliyet_alani || m.category_name || '',
            category: businessInfo.category || businessInfo.kategori || m.category || '',
            // Tables count is now deferred or loaded on demand to speed up login
            tables_count: 0 
          };
        } catch (pErr) {
          console.warn(`⚠️ Error enriching business ${m.business_id}:`, pErr.message);
          return {
            id: m.business_id,
            name: m.business_name || 'İşletme',
            sector: m.sector || 'food_beverage',
            role: m.role,
            tenant_id: m.tenant_id
          };
        }
      }));

      const filteredBusinesses = businesses.filter(Boolean);
      console.log(`✅ HibritService - Successfully loaded ${filteredBusinesses.length} unique businesses`);
      return filteredBusinesses;

    } catch (error) {
      console.error('❌ Error getting local businesses:', error);
      return [];
    }
  },

  // Get business info from hibrit Firebase Collection Architecture
  async getBusinessInfo(businessId) {
    try {
      console.log('🔍 Getting business info for:', businessId);

      // Try to get business info from new hibrit structure first
      const sectors = ['food_beverage', 'retail', 'service'];

      for (const sector of sectors) {
        try {
          const businessDoc = await getDoc(doc(db, `businesses/${sector}/businesses`, businessId));
          if (businessDoc.exists()) {
            console.log(`✅ Found business info from hibrit structure (${sector})`);
            return businessDoc;
          }
        } catch (error) {
          console.log(`⚠️ No business found in ${sector} sector`);
        }
      }

      // Fallback to old business_info collection
      const businessInfoDoc = await getDoc(doc(db, 'business_info', businessId));

      if (businessInfoDoc.exists()) {
        console.log('✅ Found business info from legacy business_info structure');
        return businessInfoDoc;
      } else {
        console.log('⚠️ No business info found in legacy structure either, checking business_settings...');

        // Fallback to old business_settings collection
        const legacyDoc = await getDoc(doc(db, 'business_settings', businessId));

        if (legacyDoc.exists()) {
          console.log('✅ Found business info from legacy business_settings structure');
          return legacyDoc;
        } else {
          console.log('❌ No business info found in any collection');
          return { exists: () => false, data: () => ({}) };
        }
      }
    } catch (error) {
      console.error('❌ Error getting business info:', error);
      throw error;
    }
  },

  // Get business advanced settings
  async getBusinessAdvancedSettings(businessId) {
    try {
      console.log('🔍 Getting advanced settings for:', businessId);

      const advancedDoc = await getDoc(doc(db, 'business_advanced_settings', businessId));

      if (advancedDoc.exists()) {
        console.log('✅ Found advanced settings from hibrit structure');
        return advancedDoc;
      } else {
        console.log('⚠️ No advanced settings found');
        return { exists: () => false, data: () => ({}) };
      }
    } catch (error) {
      console.error('❌ Error getting advanced settings:', error);
      throw error;
    }
  },

  // Get business tables from hibrit pattern
  async getBusinessTables(businessId) {
    try {
      console.log('🔍 Getting tables for business via hibrit pattern:', businessId);

      // Try new hibrit structure first
      const sectors = ['food_beverage', 'retail', 'service'];

      for (const sector of sectors) {
        try {
          const tablesCollection = collection(db, `businesses/${sector}/businesses/${businessId}/tables`);
          const tablesSnapshot = await getDocs(tablesCollection);

          if (tablesSnapshot.size > 0) {
            console.log(`✅ Found ${tablesSnapshot.size} tables via hibrit pattern (${sector})`);
            return tablesSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              business_id: businessId // Add business_id for compatibility
            }));
          }
        } catch (error) {
          console.log(`⚠️ No tables found in ${sector} hibrit pattern`);
        }
      }

      // Fallback: Use old restaurants collection hibrit pattern
      const tablesCollection = collection(db, 'restaurants', businessId, 'tables');
      const tablesSnapshot = await getDocs(tablesCollection);

      console.log(`✅ Found ${tablesSnapshot.size} tables via legacy restaurants pattern`);

      return tablesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        business_id: businessId // Add business_id for compatibility
      }));
    } catch (error) {
      console.error('❌ Error getting tables via hibrit pattern:', error);
      // Return empty array instead of throwing to prevent app crashes
      return [];
    }
  },

  // Fallback: Get businesses from old structure
  async getBusinessesFromLegacy() {
    console.log('🔄 Falling back to legacy brands collection...');

    try {
      const brandsSnapshot = await getDocs(collection(db, 'brands'));
      return brandsSnapshot.docs.map(doc => ({
        id: doc.id,
        _id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting legacy brands:', error);
      return [];
    }
  }
};